/**
 * White Label Context Provider
 * Provides tenant branding configuration across the entire application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { isPlatformAdmin } from '../auth/permissionsConfig';

// Default branding configuration (HaliSoft defaults)
const defaultBranding = {
  id: null,
  tenantCode: 'halisoft',
  companyName: 'HaliTrade',
  legalName: 'HaliSoft Ltd',

  // Colors
  primaryColor: '#DC2626',
  secondaryColor: '#B91C1C',
  accentColor: '#EF4444',
  backgroundColor: '#111827',
  textColor: '#FFFFFF',

  // Logos
  logoUrl: '/assets/halitrade-logo-red.svg',
  logoDarkUrl: '/assets/halitrade-logo-red.svg',
  faviconUrl: '/favicon.svg',
  emailLogoUrl: null,
  loginBgImageUrl: null,

  // Text
  tagline: 'Trade Finance Platform',
  poweredBy: 'powered by HaliSoft',
  footerText: '',

  // Email
  emailFromName: 'HaliTrade',
  emailFromAddress: 'noreply@halisoft.biz',
  useCustomEmails: true,

  // Features
  features: {
    showFactoring: true,
    showInvestments: true,
    showMarketplace: true,
    showWallet: true,
    showDocuments: true,
    showAnalytics: true,
    showChat: true,
    allowCustomerSignup: true,
    allowInvestorSignup: true,
    allowAgentSignup: true
  },

  // Component name mappings
  componentNames: {
    HaliVault: { display: 'Document Vault', icon: 'vault' },
    HaliPass: { display: 'Secure Login', icon: 'lock' },
    HaliFlow: { display: 'Application Process', icon: 'flow' },
    HaliScore: { display: 'Credit Assessment', icon: 'chart' },
    HaliDoc: { display: 'Document Center', icon: 'file' },
    HaliAgent: { display: 'AI Assistant', icon: 'bot' }
  },

  // Contact
  supportEmail: 'support@halisoft.biz',
  supportPhone: '',
  websiteUrl: 'https://halisoft.biz',

  // Status
  isActive: true,
  isDefault: true,
  isLoading: true
};

// Create context
const WhiteLabelContext = createContext({
  branding: defaultBranding,
  isLoading: true,
  error: null,
  refreshBranding: () => {},
  getComponentName: () => '',
  hasFeature: () => true,
  applyTheme: () => {},
  // Profile management
  availableProfiles: [],
  currentProfileCode: 'halisoft',
  switchProfile: () => {},
  // Shorthand accessors
  primaryColor: '#DC2626',
  secondaryColor: '#B91C1C',
  accentColor: '#EF4444',
  companyName: 'HaliTrade',
  logoUrl: '/assets/halitrade-logo-red.svg',
  tagline: 'Trade Finance Platform'
});

/**
 * White Label Provider Component
 */
export const WhiteLabelProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [currentProfileCode, setCurrentProfileCode] = useState('halisoft');
  const { isAuthenticated, user } = useAuth();
  const hasLoadedFromDb = useRef(false);

  /**
   * Detect tenant from URL/hostname
   */
  const detectTenant = useCallback(() => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // Check for custom domain
    if (!hostname.includes('localhost') && !hostname.includes('halisoft.biz')) {
      return hostname; // Custom domain
    }

    // Check subdomain
    const subdomain = hostname.split('.')[0];
    if (subdomain && subdomain !== 'www' && subdomain !== 'app' && subdomain !== 'localhost') {
      return subdomain;
    }

    // Check URL path for tenant hint
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      const tenantPaths = ['halitrade', 'haliagent', 'halilab', 'halifund', 'platform', 'investor', 'referral'];
      if (tenantPaths.includes(pathParts[0])) {
        return pathParts[0];
      }
    }

    return 'halisoft'; // Default
  }, [isAuthenticated]);

  /**
   * Fetch branding configuration from API
   */
  const fetchBranding = useCallback(async (skipDbCheck = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Try to load from database first (if user is authenticated)
      if (!skipDbCheck) {
        try {
          const prefResponse = await api.get('/api/white-label/user-preference');
          const prefCode = prefResponse.data?.preferred_tenant_code;

          // 'none' or 'default' means user explicitly chose NO white label (plain look, no colors)
          if (prefCode === 'none' || prefCode === 'default') {
            // Debug only: console.log('[WhiteLabelContext] DB preference is "none"');
            setCurrentProfileCode('none');
            // No primaryColor = no colored top bar
            setBranding({
              ...defaultBranding,
              id: null,
              tenantCode: 'none',
              primaryColor: null,
              secondaryColor: null,
              accentColor: null,
              isLoading: false
            });
            localStorage.setItem('selectedWhiteLabelProfile', 'none');
            setIsLoading(false);
            return;
          }

          if (prefCode) {
            // Debug only: console.log('[WhiteLabelContext] Found user preference in DB:', prefCode);
            // Use the DB preference
            const response = await api.get(`/api/white-label/tenant/${prefCode}`);
            if (response.data) {
              const data = response.data;
              const actualTenantCode = data.tenant_code;
              setCurrentProfileCode(actualTenantCode);
              localStorage.setItem('selectedWhiteLabelProfile', actualTenantCode);
              setBranding({
                id: data.id,
                tenantCode: data.tenant_code,
                companyName: data.company_name || defaultBranding.companyName,
                legalName: data.legal_name,
                primaryColor: data.primary_color || defaultBranding.primaryColor,
                secondaryColor: data.secondary_color || defaultBranding.secondaryColor,
                accentColor: data.accent_color || defaultBranding.accentColor,
                backgroundColor: data.background_color || defaultBranding.backgroundColor,
                textColor: data.text_color || defaultBranding.textColor,
                logoUrl: data.logo_url || defaultBranding.logoUrl,
                logoDarkUrl: data.logo_dark_url || defaultBranding.logoDarkUrl,
                faviconUrl: data.favicon_url || defaultBranding.faviconUrl,
                emailLogoUrl: data.email_logo_url,
                loginBgImageUrl: data.login_bg_image_url,
                tagline: data.tagline || defaultBranding.tagline,
                poweredBy: data.powered_by || '',
                footerText: data.footer_text || '',
                emailFromName: data.email_from_name || defaultBranding.companyName,
                emailFromAddress: data.email_from_address,
                useCustomEmails: data.use_custom_emails !== false,
                features: typeof data.features === 'object' ? data.features : defaultBranding.features,
                componentNames: typeof data.component_names === 'object' ? data.component_names : defaultBranding.componentNames,
                supportEmail: data.support_email || defaultBranding.supportEmail,
                supportPhone: data.support_phone,
                websiteUrl: data.website_url,
                addressLine1: data.address_line1,
                addressLine2: data.address_line2,
                city: data.city,
                state: data.state,
                postalCode: data.postal_code,
                country: data.country,
                socialLinks: data.social_links || {},
                isActive: data.is_active,
                isDefault: data.is_default,
                isLoading: false
              });
              setIsLoading(false);
              return;
            }
          }
        } catch (dbErr) {
          // Debug only: console.log('[WhiteLabelContext] Could not load from DB:', dbErr.message);
        }
      }

      // Fallback: Check localStorage for persisted profile preference
      const storedProfile = localStorage.getItem('selectedWhiteLabelProfile');
      // Debug only: console.log('[WhiteLabelContext] fetchBranding - storedProfile:', storedProfile);

      // If localStorage says 'none', that means user explicitly chose NO white label
      if (storedProfile === 'none') {
        // Debug only: console.log('[WhiteLabelContext] localStorage says "none"');
        setCurrentProfileCode('none');
        setBranding({
          ...defaultBranding,
          id: null,
          tenantCode: 'none',
          primaryColor: null,
          secondaryColor: null,
          accentColor: null,
          isLoading: false
        });
        setIsLoading(false);
        return;
      }

      const detectedTenant = detectTenant();
      const tenantCode = storedProfile && detectedTenant === 'halisoft'
        ? storedProfile
        : detectedTenant;
      // Debug only: console.log('[WhiteLabelContext] fetchBranding - using tenantCode:', tenantCode);

      // Try to fetch from API
      const looksLikeHostname = typeof tenantCode === 'string' && tenantCode.includes('.');
      const response = looksLikeHostname
        ? await api.get('/api/white-label/current')
        : await api.get(`/api/white-label/tenant/${tenantCode}`);
      // Debug only: console.log('[WhiteLabelContext] fetchBranding - API returned tenant:', response.data?.tenant_code);

      if (response.data) {
        const data = response.data;
        // Update currentProfileCode to match what was actually returned (not what we requested)
        // This handles cases where API returns a different tenant (e.g., default)
        const actualTenantCode = data.tenant_code || tenantCode;
        setCurrentProfileCode(actualTenantCode);
        // Also update localStorage to persist the actual profile we received
        if (actualTenantCode) {
          localStorage.setItem('selectedWhiteLabelProfile', actualTenantCode);
        }
        setBranding({
          id: data.id,
          tenantCode: data.tenant_code,
          companyName: data.company_name || defaultBranding.companyName,
          legalName: data.legal_name,

          primaryColor: data.primary_color || defaultBranding.primaryColor,
          secondaryColor: data.secondary_color || defaultBranding.secondaryColor,
          accentColor: data.accent_color || defaultBranding.accentColor,
          backgroundColor: data.background_color || defaultBranding.backgroundColor,
          textColor: data.text_color || defaultBranding.textColor,

          logoUrl: data.logo_url || defaultBranding.logoUrl,
          logoDarkUrl: data.logo_dark_url || defaultBranding.logoDarkUrl,
          faviconUrl: data.favicon_url || defaultBranding.faviconUrl,
          emailLogoUrl: data.email_logo_url,
          loginBgImageUrl: data.login_bg_image_url,

          tagline: data.tagline || defaultBranding.tagline,
          poweredBy: data.powered_by || '',
          footerText: data.footer_text || '',

          emailFromName: data.email_from_name || defaultBranding.companyName,
          emailFromAddress: data.email_from_address,
          useCustomEmails: data.use_custom_emails !== false,

          features: typeof data.features === 'object' ? data.features : defaultBranding.features,
          componentNames: typeof data.component_names === 'object' ? data.component_names : defaultBranding.componentNames,

          supportEmail: data.support_email || defaultBranding.supportEmail,
          supportPhone: data.support_phone,
          websiteUrl: data.website_url,

          // Contact info
          addressLine1: data.address_line1,
          addressLine2: data.address_line2,
          city: data.city,
          state: data.state,
          postalCode: data.postal_code,
          country: data.country,
          socialLinks: data.social_links || {},

          isActive: data.is_active,
          isDefault: data.is_default,
          isLoading: false
        });
      }
    } catch (err) {
      // Debug only: console.log('White label config not found, using defaults');
      setBranding({ ...defaultBranding, isLoading: false });
    } finally {
      setIsLoading(false);
    }
  }, [detectTenant]);

  /**
   * Apply theme colors to CSS variables
   */
  const applyTheme = useCallback(() => {
    const root = document.documentElement;

    // Set CSS custom properties
    root.style.setProperty('--color-primary', branding.primaryColor);
    root.style.setProperty('--color-secondary', branding.secondaryColor);
    root.style.setProperty('--color-accent', branding.accentColor);
    root.style.setProperty('--color-background', branding.backgroundColor);
    root.style.setProperty('--color-text', branding.textColor);

    // Set RGB values for Tailwind opacity support
    root.style.setProperty('--color-primary-rgb', hexToRgb(branding.primaryColor));
    root.style.setProperty('--color-secondary-rgb', hexToRgb(branding.secondaryColor));
    root.style.setProperty('--color-accent-rgb', hexToRgb(branding.accentColor));

    // Update favicon (prefer tenant-aware defaults when no custom favicon is set)
    const resolveFaviconUrl = () => {
      if (branding.faviconUrl && branding.faviconUrl !== defaultBranding.faviconUrl) {
        return branding.faviconUrl;
      }
      if (typeof window === 'undefined') return branding.faviconUrl || defaultBranding.faviconUrl;
      const path = window.location.pathname || '';
      const hostname = window.location.hostname || '';
      if (path.startsWith('/haliagent') || hostname.includes('haliagent')) return '/favicon-haliagent.svg';
      if (path.startsWith('/halitrade') || path.includes('halitrade-landing') || hostname.includes('halitrade')) return '/favicon-halitrade.svg';
      if (path.startsWith('/haliflow') || path.includes('haliflow-landing') || hostname.includes('haliflow')) return '/favicon-haliflow.svg';
      if (path.startsWith('/halilab') || path.includes('halilab-landing') || hostname.includes('halilab')) return '/favicon-halilab.svg';
      return '/favicon.svg';
    };

    const resolvedFaviconUrl = resolveFaviconUrl();
    const existingFavicon = document.querySelector('link[rel="icon"]');
    if (existingFavicon) {
      existingFavicon.href = resolvedFaviconUrl;
    }
    const shortcutFavicon = document.querySelector('link[rel="shortcut icon"]');
    if (shortcutFavicon) {
      shortcutFavicon.href = resolvedFaviconUrl;
    }

    // Update document title
    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      if (path.startsWith('/haliagent')) {
        document.title = path.includes('/login') ? 'HaliAgent - Login' : 'HaliAgent';
        return;
      }
      if (path.startsWith('/halitrade')) {
        document.title = path.includes('/login') ? 'HaliTrade - Sign Up' : 'HaliTrade';
        return;
      }
    }
    const defaultTenantCodes = new Set(['halitrade', 'halisoft', 'none', null, undefined]);
    if (defaultTenantCodes.has(branding.tenantCode)) {
      document.title = 'HaliTrade';
    } else {
      document.title = `${branding.companyName} - ${branding.tagline}`;
    }
  }, [branding]);

  /**
   * Get display name for a component
   */
  const getComponentName = useCallback((technicalName) => {
    const mapping = branding.componentNames[technicalName];
    if (mapping) {
      return typeof mapping === 'string' ? mapping : mapping.display;
    }
    return technicalName;
  }, [branding.componentNames]);

  /**
   * Check if a feature is enabled
   */
  const hasFeature = useCallback((featureName) => {
    return branding.features[featureName] !== false;
  }, [branding.features]);

  /**
   * Refresh branding from server
   */
  const refreshBranding = useCallback(() => {
    fetchBranding();
  }, [fetchBranding]);

  /**
   * Fetch all available white-label profiles
   */
  const fetchAvailableProfiles = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setAvailableProfiles([]);
        return;
      }
      const canListProfiles = isPlatformAdmin(user);
      // Debug only: console.log('[WhiteLabelContext] Fetching available profiles...');
      const response = canListProfiles
        ? await api.get('/api/white-label/tenants')
        : await api.get('/api/white-label/current-user');
      // Debug only: console.log('[WhiteLabelContext] Profiles response:', response.data);
      if (canListProfiles && response.data && Array.isArray(response.data)) {
        // Debug only: console.log('[WhiteLabelContext] Setting', response.data.length, 'profiles');
        setAvailableProfiles(response.data);
        return;
      }
      if (!canListProfiles && response.data) {
        setAvailableProfiles([response.data]);
      }
    } catch (err) {
      // Debug only: console.log('[WhiteLabelContext] Could not fetch available profiles:', err.message);
      setAvailableProfiles([]);
    }
  }, [isAuthenticated, user]);

  /**
   * Load user's preferred profile from database
   */
  const loadUserPreference = useCallback(async () => {
    try {
      // Debug only: console.log('[WhiteLabelContext] Loading user preference from database...');
      const response = await api.get('/api/white-label/user-preference');
      if (response.data?.preferred_tenant_code) {
        // Debug only: console.log('[WhiteLabelContext] User has preferred tenant:', response.data.preferred_tenant_code);
        // Switch to the user's preferred profile
        await switchProfileInternal(response.data.preferred_tenant_code, false); // false = don't save back to DB
        return response.data.preferred_tenant_code;
      }
      return null;
    } catch (err) {
      // Debug only: console.log('[WhiteLabelContext] Could not load user preference:', err.message);
      return null;
    }
  }, []);

  /**
   * Internal switch profile function
   * @param tenantCode - The tenant code to switch to
   * @param saveToDb - Whether to save the preference to the database
   */
  const switchProfileInternal = useCallback(async (tenantCode, saveToDb = true) => {
    // Debug only: console.log('[WhiteLabelContext] switchProfileInternal called with:', { tenantCode, saveToDb });
    try {
      setIsLoading(true);

      // Handle 'none' = NO white label branding at all (plain default look)
      // This is different from defaultBranding which has HaliSoft colors
      if (tenantCode === 'none' || tenantCode === 'default') {
        // Debug only: console.log('[WhiteLabelContext] Setting to NO white label');
        setCurrentProfileCode('none');
        // Use a minimal branding with NO primaryColor - this prevents the colored top bar
        setBranding({
          ...defaultBranding,
          id: null,
          tenantCode: 'none',
          primaryColor: null,  // No primary color = no colored top bar
          secondaryColor: null,
          accentColor: null,
          isLoading: false
        });
        localStorage.setItem('selectedWhiteLabelProfile', 'none');
        if (saveToDb) {
          try {
            // Debug only: console.log('[WhiteLabelContext] Saving "none" to DB');
            const saveResp = await api.put('/api/white-label/user-preference', { tenant_code: 'none' });
            // Debug only: console.log('[WhiteLabelContext] Save response:', saveResp.data);
          } catch (e) {
            // Debug only: console.log('[WhiteLabelContext] Could not save preference to DB:', e.message);
          }
        }
        setIsLoading(false);
        return;
      }

      const response = await api.get(`/api/white-label/tenant/${tenantCode}`);

      if (response.data) {
        const data = response.data;
        const actualTenantCode = data.tenant_code || tenantCode;
        setCurrentProfileCode(actualTenantCode);
        setBranding({
          id: data.id,
          tenantCode: data.tenant_code,
          companyName: data.company_name || defaultBranding.companyName,
          legalName: data.legal_name,

          primaryColor: data.primary_color || defaultBranding.primaryColor,
          secondaryColor: data.secondary_color || defaultBranding.secondaryColor,
          accentColor: data.accent_color || defaultBranding.accentColor,
          backgroundColor: data.background_color || defaultBranding.backgroundColor,
          textColor: data.text_color || defaultBranding.textColor,

          logoUrl: data.logo_url || defaultBranding.logoUrl,
          logoDarkUrl: data.logo_dark_url || defaultBranding.logoDarkUrl,
          faviconUrl: data.favicon_url || defaultBranding.faviconUrl,
          emailLogoUrl: data.email_logo_url,
          loginBgImageUrl: data.login_bg_image_url,

          tagline: data.tagline || defaultBranding.tagline,
          poweredBy: data.powered_by || '',
          footerText: data.footer_text || '',

          emailFromName: data.email_from_name || defaultBranding.companyName,
          emailFromAddress: data.email_from_address,
          useCustomEmails: data.use_custom_emails !== false,

          features: typeof data.features === 'object' ? data.features : defaultBranding.features,
          componentNames: typeof data.component_names === 'object' ? data.component_names : defaultBranding.componentNames,

          supportEmail: data.support_email || defaultBranding.supportEmail,
          supportPhone: data.support_phone,
          websiteUrl: data.website_url,

          addressLine1: data.address_line1,
          addressLine2: data.address_line2,
          city: data.city,
          state: data.state,
          postalCode: data.postal_code,
          country: data.country,
          socialLinks: data.social_links || {},

          isActive: data.is_active,
          isDefault: data.is_default,
          isLoading: false
        });

        // Store the selected profile in localStorage (as fallback)
        localStorage.setItem('selectedWhiteLabelProfile', actualTenantCode);

        // Save to database if requested
        if (saveToDb) {
          try {
            // Debug only: console.log('[WhiteLabelContext] Saving to DB - tenant_code:', actualTenantCode);
            const saveResponse = await api.put('/api/white-label/user-preference', { tenant_code: actualTenantCode });
            // Debug only: console.log('[WhiteLabelContext] Saved profile preference to database:', actualTenantCode);
          } catch (e) {
            console.error('[WhiteLabelContext] Could not save preference to DB:', e.message, e.response?.data);
          }
        }
      }
    } catch (err) {
      console.error('Failed to switch profile:', err);
      setError('Failed to switch profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Switch to a different white-label profile (public API)
   */
  const switchProfile = useCallback(async (tenantCode) => {
    return switchProfileInternal(tenantCode, true);
  }, [switchProfileInternal]);

  // Fetch branding on mount (skip DB check initially, use localStorage/detectTenant)
  useEffect(() => {
    // Debug only: console.log('[WhiteLabelContext] Provider mounted, fetching data...');
    // On initial mount, skip DB check since auth may not be ready yet
    fetchBranding(true); // skipDbCheck = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When authentication becomes available, load from DB
  useEffect(() => {
    if (isAuthenticated) {
      fetchAvailableProfiles();
    }
    if (isAuthenticated && user?.id && !hasLoadedFromDb.current) {
      // Debug only: console.log('[WhiteLabelContext] User authenticated, loading preference from DB...');
      hasLoadedFromDb.current = true;

      // Load the user's preferred profile from the database
      const loadFromDb = async () => {
        try {
          const response = await api.get('/api/white-label/user-preference');
          const prefCode = response.data?.preferred_tenant_code;

          if (prefCode === 'none' || prefCode === 'default') {
            // User explicitly chose "no white label" - NO colors (plain look)
            // Debug only: console.log('[WhiteLabelContext] DB preference is "none"');
            setCurrentProfileCode('none');
            setBranding({
              ...defaultBranding,
              id: null,
              tenantCode: 'none',
              primaryColor: null,
              secondaryColor: null,
              accentColor: null,
              isLoading: false
            });
            localStorage.setItem('selectedWhiteLabelProfile', 'none');
          } else if (prefCode) {
            // Debug only: console.log('[WhiteLabelContext] Found DB preference:', prefCode);
            // Switch to the user's preferred profile (don't save back to DB)
            await switchProfileInternal(prefCode, false);
          } else {
            // Debug only: console.log('[WhiteLabelContext] No DB preference found');
          }
        } catch (err) {
          // Debug only: console.log('[WhiteLabelContext] Error loading DB preference:', err.message);
        }
      };

      loadFromDb();
    }

    // Reset the flag when user logs out
    if (!isAuthenticated) {
      hasLoadedFromDb.current = false;
    }
  }, [isAuthenticated, user?.id, switchProfileInternal]);

  // Apply theme when branding changes
  useEffect(() => {
    if (!isLoading) {
      applyTheme();
    }
  }, [branding, isLoading, applyTheme]);

  // Context value
  const value = {
    branding,
    isLoading,
    error,
    refreshBranding,
    getComponentName,
    hasFeature,
    applyTheme,

    // Profile management
    availableProfiles,
    currentProfileCode,
    switchProfile,
    fetchAvailableProfiles, // Expose for retry when user is authenticated
    loadUserPreference, // Load user's preferred profile from database

    // Shorthand accessors
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
    accentColor: branding.accentColor,
    companyName: branding.companyName,
    logoUrl: branding.logoUrl,
    tagline: branding.tagline
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

/**
 * Hook to use white label context
 */
export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    console.warn('useWhiteLabel must be used within a WhiteLabelProvider');
    return { branding: defaultBranding, isLoading: false };
  }
  return context;
};

/**
 * HOC to inject white label props
 */
export const withWhiteLabel = (Component) => {
  return function WhiteLabelWrapper(props) {
    const whiteLabel = useWhiteLabel();
    return <Component {...props} whiteLabel={whiteLabel} />;
  };
};

/**
 * Helper: Convert hex to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
  return '220, 38, 38'; // Default red
}

/**
 * Component to display branded logo
 */
export const BrandedLogo = ({ className = '', darkMode = false, size = 'md' }) => {
  const { branding } = useWhiteLabel();

  const sizeClasses = {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-10',
    xl: 'h-12'
  };

  const logoUrl = darkMode ? branding.logoDarkUrl : branding.logoUrl;

  if (!logoUrl) {
    return (
      <span className={`font-bold ${sizeClasses[size]} flex items-center ${className}`}>
        {branding.companyName}
      </span>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={branding.companyName}
      className={`${sizeClasses[size]} object-contain ${className}`}
    />
  );
};

/**
 * Component to display powered by text
 */
export const PoweredBy = ({ className = '' }) => {
  const { branding } = useWhiteLabel();

  if (!branding.poweredBy) return null;

  return (
    <span className={`text-xs opacity-60 ${className}`}>
      {branding.poweredBy}
    </span>
  );
};

export default WhiteLabelContext;
